import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appCurrencyMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyMaskDirective),
      multi: true
    }
  ]
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef) {}

  writeValue(value: number | null): void {
    if (value !== null && value !== undefined) {
      this.el.nativeElement.value = this.formatToCurrency(value);
    } else {
      this.el.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove todos os caracteres não numéricos
    value = value.replace(/\D/g, '');

    if (value === '') {
      this.onChange(null);
      input.value = '';
      return;
    }

    // Converte para número decimal (divide por 100 para considerar centavos)
    const numericValue = parseFloat(value) / 100;

    // Formata e atualiza o input
    input.value = this.formatToCurrency(numericValue);

    // Notifica o FormControl
    this.onChange(numericValue);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  @HostListener('focus', ['$event'])
  onFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Remove formatação ao focar para facilitar edição
    if (input.value) {
      const numericValue = this.parseFromCurrency(input.value);
      if (numericValue !== null) {
        // Mantém a formatação ao focar
        input.value = this.formatToCurrency(numericValue);
      }
    }
  }

  private formatToCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  private parseFromCurrency(value: string): number | null {
    if (!value) return null;
    
    // Remove o símbolo de moeda e espaços
    const cleanValue = value
      .replace('R$', '')
      .replace(/\s/g, '')
      .replace('.', '')
      .replace(',', '.');
    
    const numericValue = parseFloat(cleanValue);
    return isNaN(numericValue) ? null : numericValue;
  }
}
